import apiServices from "../apiServices";
import { validateOrThrowApiResponse } from "../response-validator";

type CreateGamePayload = {
    create_by?: string;
    exercise_name: string;
    description?: string;
    subject: string;
    group_id?: number | string;
    teacher_uuid?: string;
    question_type: string;
    game_type: string;
    game_default?: boolean;
    thumbnail?: File | Blob | null;
    thumbnailFileName?: string;
};

type UpdateExerciseNameAndSuggestionPayload = {
    gameId: number | string;
    exercise_name?: string;
    suggestion?: string;
    description?: string;
};

type GenerateUUIDForPIlaPayload =
    | { uuid_gameinfo: string; uuid_sequenceinfo?: undefined; teacher_uuid?: string }
    | { uuid_sequenceinfo: string; uuid_gameinfo?: undefined; teacher_uuid?: string };

const mainAPI = {

    createGame(payload: CreateGamePayload) {
        const formData = new FormData();
        formData.append("create_by", payload.create_by || "");
        formData.append("exercise_name", payload.exercise_name);
        formData.append("description", payload.description || "");
        formData.append("subject", payload.subject);
        if (payload.group_id) {
            formData.append("group_id", payload.group_id.toString());
        }
        if (payload.teacher_uuid) {
            formData.append("teacher_uuid", payload.teacher_uuid);
        }
        formData.append("question_type", payload.question_type);
        formData.append("game_type", payload.game_type);
        if (payload.game_default !== undefined) {
            formData.append("game_default", String(payload.game_default));
        }

        if (payload.thumbnail) {
            if (payload.thumbnailFileName) {
                formData.append("thumbnail", payload.thumbnail, payload.thumbnailFileName);
            } else {
                formData.append("thumbnail", payload.thumbnail);
            }
        }

        return apiServices
            .post(`/api/game`, formData, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error createGame:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    getAllLessonLibrary() {
        return apiServices
            .get(`/api/lesson-library`, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error getAllLessonLibrary:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    getAllLessonLibraryByTeacherUuid(teacher_uuid: string) {
        return apiServices
            .get(`/api/lesson-library/teacher/${teacher_uuid}`, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error getAllLessonLibraryByTeacherUuid:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    getAllLessonLibraryGameDefault() {
        return apiServices
            .get(`/api/lesson-library/default-games`, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error getAllLessonLibraryGameDefault:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    getAllLessonLibraryExceptDefaultGames() {
        return apiServices
            .get(`/api/lesson-library/except-default-games`, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error getAllLessonLibraryExceptDefaultGames:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    getGameinfoAndQuestionByUuid(uuid: string) {
        return apiServices
            .get(`/api/game/uuid/${uuid}`, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error getGameinfoAndQuestionByUuid:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    updateExerciseNameAndSuggestion(payload: UpdateExerciseNameAndSuggestionPayload) {
        const requestBody: { exercise_name?: string; suggestion?: string; description?: string } = {};
        if (payload.exercise_name !== undefined) {
            requestBody.exercise_name = payload.exercise_name;
        }
        if (payload.suggestion !== undefined) {
            requestBody.suggestion = payload.suggestion;
        }
        if (payload.description !== undefined) {
            requestBody.description = payload.description;
        }

        return apiServices
            .patch(`/api/game/${payload.gameId}/exerciseNameAndSuggestion`, requestBody, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error updateExerciseNameAndSuggestion:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    softDeleteGameById(id: string) {
        return apiServices
            .patch(`/api/game/${id}/soft-delete`, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error softDeleteGameById:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    generateUUIDForPIla(payload: GenerateUUIDForPIlaPayload) {
        return apiServices
            .post(`/api/game/uuid-for-pila`, payload, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error generateUUIDForPIla:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },
}

export default mainAPI;